import { Check, CheckCircle, AlertCircle, Phone, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { Reservation } from '../../types';
import {
  getCustomerPhoneForDisplay,
  reservationHasUserFacingNotes,
} from '../../lib/customerClassification';

const STATUS_LEFT_BAR: Record<Reservation['status'], string> = {
  confirmed: 'bg-blue-500',
  'in-progress': 'bg-amber-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-400',
  'no-show': 'bg-gray-400',
};

const STATUS_LABEL: Record<Reservation['status'], string> = {
  confirmed: 'Επιβεβαιωμένη',
  'in-progress': 'Σε εξέλιξη',
  completed: 'Ολοκληρωμένη',
  cancelled: 'Ακυρωμένη',
  'no-show': 'Δεν εμφανίστηκε',
};

const STATUS_BADGE: Record<Reservation['status'], string> = {
  confirmed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'in-progress': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'no-show': 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400',
};

function renderTableLabel(reservation: Reservation): string {
  const tables = reservation.tables ?? [];
  if (tables.length > 1) {
    return `Τρ. ${tables.map(t => t.number).join(', ')}`;
  }
  if (reservation.table) {
    return `Τρ. ${reservation.table.number}`;
  }
  return 'Αυτόματη';
}

interface MobileReservationRowProps {
  reservation: Reservation;
  onTap: (reservation: Reservation) => void;
  onCheckIn: (id: string) => void;
  onComplete: (id: string) => void;
}

export function MobileReservationRow({
  reservation,
  onTap,
  onCheckIn,
  onComplete,
}: MobileReservationRowProps) {
  const phone = getCustomerPhoneForDisplay(reservation.customer);
  const hasNotes = reservationHasUserFacingNotes(reservation);
  const tableLabel = renderTableLabel(reservation);

  const showPrimaryAction =
    reservation.status === 'confirmed' ||
    reservation.status === 'in-progress';

  return (
    <div
      id={`reservation-row-${reservation.id}`}
      className="relative flex items-stretch cursor-pointer active:bg-accent/60 transition-colors"
      onClick={() => onTap(reservation)}
    >
      {/* Status left bar */}
      <div className={cn('w-1 shrink-0 rounded-l-sm', STATUS_LEFT_BAR[reservation.status])} />

      {/* Main content */}
      <div className="flex-1 min-w-0 px-3 py-2.5 flex items-center gap-2.5">
        {/* Time */}
        <span className="font-mono text-sm font-semibold text-foreground shrink-0 w-[3.25rem]">
          {reservation.time}
        </span>

        {/* Name + details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-medium text-sm text-foreground truncate">
              {reservation.customer?.name || 'Άγνωστος'}
            </span>
            {hasNotes && (
              <AlertCircle className="h-3.5 w-3.5 text-orange-500 shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-1.5 py-0.5">
              <Users className="h-3 w-3 shrink-0" />
              {reservation.party_size}
            </span>
            <span className="inline-flex items-center rounded-full bg-muted/60 px-1.5 py-0.5 truncate max-w-[6.5rem]">
              {tableLabel}
            </span>
            {phone && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-1.5 py-0.5 truncate max-w-[8rem]">
                <Phone className="h-3 w-3 shrink-0" />
                {phone}
              </span>
            )}
          </div>
        </div>

        {/* Status badge */}
        <span
          className={cn(
            'shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-tight border border-transparent',
            STATUS_BADGE[reservation.status]
          )}
        >
          {STATUS_LABEL[reservation.status]}
        </span>

        {/* Primary contextual action */}
        {showPrimaryAction && (
          <div className="shrink-0" onClick={e => e.stopPropagation()}>
            {reservation.status === 'confirmed' && (
              <button
                onClick={() => onCheckIn(reservation.id)}
                className="h-8 w-8 rounded-xl bg-green-500/15 border border-green-500/25 flex items-center justify-center text-green-500 touch-target transition-[background-color,transform] duration-150 hover:bg-green-500/20 active:scale-[0.96]"
                title="Check-in"
              >
                <CheckCircle className="h-[18px] w-[18px]" />
              </button>
            )}
            {reservation.status === 'in-progress' && (
              <button
                onClick={() => onComplete(reservation.id)}
                className="h-8 w-8 rounded-xl bg-violet-600 border border-violet-400/60 text-white shadow-sm shadow-violet-900/30 flex items-center justify-center touch-target transition-[background-color,transform] duration-150 hover:bg-violet-500 active:scale-[0.96]"
                title="Ολοκληρώθηκε"
              >
                <Check className="h-[18px] w-[18px]" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
